import { HttpErrorResponse } from '@angular/common/http';

/**
 * ⚠️ `ErrorDeApi` y `esNoEncontrado` YA NO VIVEN AQUI.
 *
 * Nacieron en este archivo, y desde `@intelishs-group/schema-gen` 1.1.0 los
 * EMITE el generador dentro de `api-base.service.ts`, junto a la base que los
 * lanza. Asi los dos se mueven juntos y el resto de la plataforma los recibe
 * sin copiarlos.
 *
 *     import { ErrorDeApi, esNoEncontrado } from '@services/api-base.service';
 *
 * ⚠️ NO los redeclares aqui «por comodidad»: serian DOS clases distintas con el
 * mismo nombre, y entonces `instanceof` devuelve false contra la que lanza la
 * base. Eso no da error de compilacion ni rompe ninguna prueba que mockee el
 * servicio —comprobado: 69/69 seguian en verde con el duplicado puesto—; lo
 * unico que pasa es que un 404 deja de reconocerse y vuelve a salir como fallo.
 */
interface ApiErrorResponse {
  errored?: boolean;
  errorMessage?: string | null;
  errors?: string[] | null;
}

/**
 * Extrae el mensaje de error de una respuesta API envuelta.
 * Retorna null si la respuesta no tiene errores.
 */
export function extractApiError(response: ApiErrorResponse): string | null {
  if (!response.errored) return null;
  const details = response.errors?.length ? `\n${response.errors.join('\n')}` : '';
  return `${response.errorMessage ?? 'Error desconocido'}${details}`;
}

/**
 * Valida una respuesta cruda (`unknown`) de un endpoint sin response tipado en
 * el spec OpenAPI (generado como `http.<verbo><unknown>`). Desenvuelve el
 * wrapper ResponseDto si está presente y lanza `Error` con el mensaje del
 * backend/PAC cuando hay fallo, aunque venga con HTTP 200:
 * 1. Wrapper con `errored: true` (error reportado por el backend).
 * 2. `errors[]` con elementos dentro del objeto interno
 *    (errores del PAC que llegan con `errored: false`).
 *
 * Una respuesta vacía (200 sin body) se considera válida: es el caso normal
 * de endpoints void. Si el endpoint debe devolver datos, valida el retorno
 * en el caller (p. ej. `unwrapStampResponse`).
 *
 * @param raw - Respuesta cruda del endpoint
 * @returns El objeto interno (`object` del wrapper) o la respuesta tal cual
 * @throws Error con el mensaje legible del backend/PAC
 */
export function unwrapApiResponse(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') {
    return raw;
  }

  let inner: unknown = raw;
  if ('object' in raw || 'errored' in raw) {
    const wrapper = raw as ApiErrorResponse & { object?: unknown };
    const apiError = extractApiError(wrapper);
    if (apiError) throw new Error(apiError);
    inner = wrapper.object ?? {};
  }

  if (inner && typeof inner === 'object' && 'errors' in inner) {
    const { message, errors } = inner as { message?: string | null; errors?: string[] | null };
    if (errors?.length) {
      throw new Error([message, ...errors].filter(Boolean).join('\n'));
    }
  }

  return inner;
}

/**
 * Extrae un mensaje legible de cualquier tipo de error HTTP.
 * Soporta HttpErrorResponse, Error, y objetos con shape de API response.
 */
export function extractHttpError(error: unknown, fallback = 'Error inesperado'): string {
  if (error instanceof HttpErrorResponse) {
    const body = error.error as ApiErrorResponse | undefined;
    if (body?.errorMessage) {
      const details = body.errors?.length ? `\n${body.errors.join('\n')}` : '';
      return `${body.errorMessage}${details}`;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null) {
    const obj = error as Record<string, unknown>;
    if (typeof obj['errorMessage'] === 'string') return obj['errorMessage'];
    if (typeof obj['message'] === 'string') return obj['message'];
  }

  return fallback;
}

/**
 * Extrae un mensaje legible de un error HTTP o genérico.
 * Maneja HttpErrorResponse, Error estándar, y objetos con estructura { error: { message } }.
 *
 * @param error - El error capturado en un catch
 * @param fallback - Mensaje por defecto si no se puede extraer uno del error
 * @returns Mensaje de error legible para el usuario
 */
export function extractHttpErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    return error.error?.message ?? error.error?.errorMessage ?? error.message ?? fallback;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null) {
    const obj = error as Record<string, unknown>;
    const innerError = obj['error'] as Record<string, unknown> | undefined;
    if (typeof innerError?.['message'] === 'string') return innerError['message'];
    if (typeof innerError?.['errorMessage'] === 'string') return innerError['errorMessage'];
    if (typeof obj['message'] === 'string') return obj['message'];
  }
  return fallback;
}
